import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, FormControlLabel, Checkbox,
  Divider, TableSortLabel, TablePagination, IconButton, Tooltip,
} from '@mui/material';
import { TrendingUp, TrendingDown, ExitToApp, Delete } from '@mui/icons-material';
import axios from 'axios';

function fmt(v) { return v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'; }

function SourceChip({ source }) {
  const isAuto = source === 'auto';
  return (
    <Chip
      label={isAuto ? 'Auto' : 'Manual'}
      size="small"
      sx={{
        fontSize: '0.65rem', fontWeight: 700, height: 20,
        bgcolor: isAuto ? 'rgba(156,39,176,0.1)' : 'rgba(33,150,243,0.1)',
        color: isAuto ? '#9C27B0' : '#2196F3',
        border: `1px solid ${isAuto ? '#9C27B0' : '#2196F3'}`,
      }}
    />
  );
}

function PnlCard({ label, value }) {
  return (
    <Paper sx={{ p: 1, textAlign: 'center', minWidth: 100 }}>
      <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
      <Typography variant="body1" fontWeight={700} color={Number(value) >= 0 ? 'success.main' : 'error.main'}>
        {fmt(value)}
      </Typography>
    </Paper>
  );
}

const PAGE_OPTS = [10, 20, 50, 100, -1];

export default function Dashboard({ user, apiUrl }) {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState(0);
  const [paperPnl, setPaperPnl] = useState({ unrealized: 0, realized: 0, dayUnrealized: 0, dayRealized: 0, total: 0 });
  const [livePnl, setLivePnl] = useState({ unrealized: 0, realized: 0, dayUnrealized: 0, dayRealized: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [showPaper, setShowPaper] = useState(true);
  const [showLive, setShowLive] = useState(true);

  const [sortOrder, setSortOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [closing, setClosing] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPnl = useCallback(async () => {
    try {
      const [paperRes, liveRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/pnl-summary`, { headers, params: { trade_mode: 'paper' } }),
        axios.get(`${apiUrl}/orders/pnl-summary`, { headers, params: { trade_mode: 'real' } }),
      ]);
      const p = paperRes.data;
      setPaperPnl({ unrealized: p.unrealized, realized: p.realized, dayUnrealized: p.day_unrealized, dayRealized: p.day_realized, total: p.total });
      const l = liveRes.data;
      setLivePnl({ unrealized: l.unrealized, realized: l.realized, dayUnrealized: l.day_unrealized, dayRealized: l.day_realized, total: l.total });
    } catch (err) { console.error(err); }
  }, [apiUrl]);

  const fetchPositions = useCallback(async () => {
    try {
      const tradeModes = [];
      if (showPaper) tradeModes.push('paper');
      if (showLive) tradeModes.push('real');
      if (tradeModes.length === 0) { setPositions([]); setTotalPositions(0); return; }

      // If both, fetch without trade_mode filter
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        status: 'open',
      };
      if (tradeModes.length === 1) params.trade_mode = tradeModes[0];

      const res = await axios.get(`${apiUrl}/orders/positions`, { headers, params });
      setPositions(res.data.items || []);
      setTotalPositions(res.data.total || 0);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiUrl, page, rowsPerPage, sortBy, sortOrder, showPaper, showLive]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);
  useEffect(() => { fetchPositions(); }, [fetchPositions]);

  const closePosition = async (id) => {
    setClosing(id);
    try {
      await axios.post(`${apiUrl}/orders/positions/${id}/close`, {}, { headers });
      fetchPositions(); fetchPnl();
    } catch (err) { console.error(err); }
    finally { setClosing(null); }
  };

  const deletePosition = async (id) => {
    if (!window.confirm('Delete this position?')) return;
    try {
      await axios.delete(`${apiUrl}/orders/positions/${id}`, { headers });
      fetchPositions(); fetchPnl();
    } catch (err) { console.error(err); }
  };

  const handleSort = (col) => {
    setSortOrder(sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortBy(col);
    setPage(0);
  };

  const handlePageChange = (e, p) => setPage(p);
  const handleRowsChange = (e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><Typography>Loading...</Typography></Box>;

  const computePnl = (p) => {
    const priceDiff = (p.current_price || 0) - (p.entry_price || 0);
    const directedDiff = (p.order_type === 'SELL' || p.order_type === 'SHORT') ? -priceDiff : priceDiff;
    return Math.round(directedDiff * (p.quantity || 0) * 100) / 100;
  };

  return (
    <Box data-testid="dashboard-page">
      <Typography variant="h5" mb={2}>Trading Dashboard</Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 1.5, borderLeft: '3px solid #3A5BA0' }}>
            <Typography variant="subtitle2" fontWeight={700} color="primary" mb={1}>Paper P&L</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <PnlCard label="Day Unrealized" value={paperPnl.dayUnrealized} />
              <PnlCard label="Day Realized" value={paperPnl.dayRealized} />
              <PnlCard label="Unrealized" value={paperPnl.unrealized} />
              <PnlCard label="Realized" value={paperPnl.realized} />
              <PnlCard label="TOTAL P&L" value={paperPnl.total} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 1.5, borderLeft: '3px solid #E8392E' }}>
            <Typography variant="subtitle2" fontWeight={700} color="secondary" mb={1}>Live P&L</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <PnlCard label="Day Unrealized" value={livePnl.dayUnrealized} />
              <PnlCard label="Day Realized" value={livePnl.dayRealized} />
              <PnlCard label="Unrealized" value={livePnl.unrealized} />
              <PnlCard label="Realized" value={livePnl.realized} />
              <PnlCard label="TOTAL P&L" value={livePnl.total} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Open Positions ({totalPositions})
          </Typography>
          <Box display="flex" gap={1}>
            <FormControlLabel
              control={<Checkbox size="small" checked={showPaper} onChange={(e) => { setShowPaper(e.target.checked); setPage(0); }} />}
              label={<Typography variant="caption">Paper</Typography>} data-testid="filter-paper-checkbox" />
            <FormControlLabel
              control={<Checkbox size="small" checked={showLive} onChange={(e) => { setShowLive(e.target.checked); setPage(0); }} />}
              label={<Typography variant="caption">Live</Typography>} data-testid="filter-live-checkbox" />
          </Box>
        </Box>
        <Divider />
        {positions.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No open positions</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'source', label: 'Source' },
                      { id: 'trade_mode', label: 'Mode' },
                      { id: 'quantity', label: 'Qty', align: 'right' },
                      { id: 'entry_price', label: 'Entry', align: 'right' },
                      { id: 'current_price', label: 'Current', align: 'right' },
                      { id: 'unrealized_pnl', label: 'P&L', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={sortBy === col.id} direction={sortBy === col.id ? sortOrder : 'asc'}
                          onClick={() => handleSort(col.id)}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map(pos => {
                    const pnl = computePnl(pos);
                    return (
                      <TableRow key={pos.id} data-testid={`dash-pos-${pos.id}`}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{pos.symbol}</Typography></TableCell>
                        <TableCell><SourceChip source={pos.source} /></TableCell>
                        <TableCell>
                          <Chip label={pos.trade_mode === 'paper' ? 'Paper' : 'Live'} size="small"
                            color={pos.trade_mode === 'paper' ? 'default' : 'primary'} />
                        </TableCell>
                        <TableCell align="right">{pos.quantity}</TableCell>
                        <TableCell align="right">{fmt(pos.entry_price)}</TableCell>
                        <TableCell align="right">{fmt(pos.current_price)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={700}
                            color={pnl >= 0 ? 'success.main' : 'error.main'}>
                            {fmt(pnl)}
                            {pnl >= 0 ? <TrendingUp sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
                              : <TrendingDown sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.3} justifyContent="center">
                            <Tooltip title="Exit Position">
                              <IconButton size="small" color="warning" onClick={() => closePosition(pos.id)}
                                disabled={closing === pos.id} data-testid={`exit-dash-pos-${pos.id}`}>
                                <ExitToApp sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Position">
                              <IconButton size="small" color="error" onClick={() => deletePosition(pos.id)}
                                data-testid={`delete-dash-pos-${pos.id}`}>
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={totalPositions} rowsPerPage={rowsPerPage} page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsChange} />
          </>
        )}
      </Paper>
    </Box>
  );
}
